import React, { useState, useRef, useEffect } from 'react';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Riddle system state
  const [currentRiddle, setCurrentRiddle] = useState<any>(null);
  const [riddleProgress, setRiddleProgress] = useState<any>(null);
  const [riddleLoading, setRiddleLoading] = useState(true);
  const [riddleError, setRiddleError] = useState('');
  
  // Riddle chat state
  const [riddleMessages, setRiddleMessages] = useState<any[]>([]);
  const [riddleInputMessage, setRiddleInputMessage] = useState('');
  const [isRiddleChatLoading, setIsRiddleChatLoading] = useState(false);
  const riddleMessagesEndRef = useRef<HTMLDivElement>(null);


  const scrollRiddleToBottom = () => {
    riddleMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollRiddleToBottom, [riddleMessages]);

  const handleAuth = async () => {
    if (!email || !password) {
      setAuthError('Please fill in all fields');
      return;
    }

    setAuthError('');
    setIsLoading(true);

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const apiUrl = process.env.REACT_APP_API_URL || 'https://pvl2xu5n49.execute-api.us-west-2.amazonaws.com/dev';
      
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      // Store the token
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userEmail', data.email);

      setIsAuthenticated(true);
      setEmail('');
      setPassword('');
      setIsLoading(false);
      
      // Load riddles after successful login
      loadRiddles().catch(console.error);
    } catch (error: any) {
      setAuthError(error.message || 'Authentication failed');
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear stored authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    
    setIsAuthenticated(false);
  };


  // Load riddle progress
  const loadRiddleProgress = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'https://pvl2xu5n49.execute-api.us-west-2.amazonaws.com/dev';
      console.log('Loading riddle progress from:', apiUrl);
      
      const response = await fetch(`${apiUrl}/riddle/progress`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRiddleProgress(data);
        console.log('Riddle progress loaded:', data);
      } else if (response.status === 401) {
        handleLogout();
        throw new Error('Authentication expired');
      } else {
        throw new Error(`HTTP Error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading riddle progress:', error);
      throw error;
    }
  };

  // Load current riddle
  const loadCurrentRiddle = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'https://pvl2xu5n49.execute-api.us-west-2.amazonaws.com/dev';
      console.log('Loading current riddle from:', apiUrl);
      
      const response = await fetch(`${apiUrl}/riddle/current`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentRiddle(data);
        console.log('Current riddle loaded:', data);
      } else if (response.status === 401) {
        handleLogout();
        throw new Error('Authentication expired');
      } else {
        throw new Error(`HTTP Error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading current riddle:', error);
      throw error;
    }
  };

  // Send riddle chat message
  const sendRiddleMessage = async () => {
    if (!riddleInputMessage.trim() || isRiddleChatLoading) return;

    const userMessage = riddleInputMessage.trim();
    setRiddleInputMessage('');
    setIsRiddleChatLoading(true);

    // Add user message to chat
    const newMessages = [...riddleMessages, { role: 'user', content: userMessage }];
    setRiddleMessages(newMessages);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'https://pvl2xu5n49.execute-api.us-west-2.amazonaws.com/dev';
      const response = await fetch(`${apiUrl}/riddle/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_history: riddleMessages
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to send riddle message');
      }

      const data = await response.json();
      setRiddleMessages(data.conversation_history || [...newMessages, { 
        role: 'assistant', 
        content: data.response 
      }]);
      
      // If they got the answer correct, reload progress
      if (data.correct) {
        loadRiddles().catch(console.error);
      }
      
      setIsRiddleChatLoading(false);
    } catch (error) {
      setRiddleMessages([...newMessages, { 
        role: 'assistant', 
        content: '⚠️ System temporarily unavailable. Please try again in a moment.' 
      }]);
      setIsRiddleChatLoading(false);
    }
  };

  // Load riddles (both progress and current)
  const loadRiddles = async () => {
    setRiddleLoading(true);
    setRiddleError('');
    
    try {
      await Promise.all([
        loadRiddleProgress(),
        loadCurrentRiddle()
      ]);
    } catch (error) {
      console.error('Error loading riddles:', error);
      setRiddleError('Failed to load riddles');
    } finally {
      setRiddleLoading(false);
    }
  };

  // Check for existing authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userEmail = localStorage.getItem('userEmail');
    
    if (token && userEmail) {
      setIsAuthenticated(true);
      loadRiddles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const handleRiddleChatKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendRiddleMessage();
    }
  };


  if (!isAuthenticated) {
    return (
      <div className="App">
        <div className="treasure-container">
          <header className="treasure-header">
            <h1>🎯 Coordinate Challenge</h1>
            <p>Break the AI. Extract the coordinates. Win $10,000.</p>
          </header>
          
          <div className="auth-container">
            <div className="auth-form">
              <h2>{authMode === 'login' ? 'Login' : 'Register'}</h2>
              {authError && <div className="auth-error">{authError}</div>}
              
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                />
              </div>
              
              <button 
                onClick={handleAuth} 
                disabled={isLoading}
                className="auth-button"
              >
                {isLoading ? 'Please wait...' : (authMode === 'login' ? 'Login' : 'Register')}
              </button>
              
              <p className="auth-switch">
                {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <span 
                  onClick={() => {
                    setAuthMode(authMode === 'login' ? 'register' : 'login');
                    setAuthError('');
                  }}
                  className="auth-link"
                >
                  {authMode === 'login' ? 'Register' : 'Login'}
                </span>
              </p>
            </div>
          </div>
          
          <footer className="treasure-footer">
            <div className="footer-content">
              <p>Built by <a href="https://stratis-ai.com" target="_blank" rel="noopener noreferrer">Stratis AI</a></p>
              <p>Contact: <a href="mailto:info@aitreasuremap.com">info@aitreasuremap.com</a></p>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="treasure-container">
        <header className="treasure-header">
          <h1>🎯 Coordinate Challenge</h1>
          <p>Break the AI. Extract the coordinates. Win $10,000.</p>
          <div className="header-controls">
            <div className="page-nav">
              <h2>🧩 Riddle Quest</h2>
            </div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </header>
        
        <div className="riddles-container">
            <div className="riddles-content">
              <h2>🧩 Riddle Quest</h2>
              <p>Solve riddles to unlock coordinate digits one by one!</p>
              
              {riddleLoading && (
                <div className="loading-section">
                  <p>🔄 Loading riddles...</p>
                </div>
              )}
              
              {riddleError && (
                <div className="error-section">
                  <p>❌ Error: {riddleError}</p>
                  <button 
                    onClick={() => {
                      loadRiddles();
                    }}
                    className="retry-button"
                  >
                    🔄 Retry
                  </button>
                </div>
              )}
              
              {!riddleLoading && !riddleError && riddleProgress && (
                <div className="progress-section">
                  <h3>📊 Your Progress</h3>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(riddleProgress.progress / riddleProgress.total_riddles) * 100}%` }}
                    ></div>
                  </div>
                  <p>{riddleProgress.progress} of {riddleProgress.total_riddles} riddles solved</p>
                  
                  <div className="coordinates-display">
                    <h4>🗺️ Unlocked Coordinates</h4>
                    <div className="coordinate-row">
                      <label>Latitude:</label>
                      <span className="coordinate-value">{riddleProgress.latitude_display}</span>
                    </div>
                    <div className="coordinate-row">
                      <label>Longitude:</label>
                      <span className="coordinate-value">{riddleProgress.longitude_display}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {!riddleLoading && !riddleError && currentRiddle && !currentRiddle.completed && (
                <div className="riddle-chat-section">
                  <h3>🧙‍♂️ Riddle Guardian</h3>
                  <div className="riddle-chat-container">
                    <div className="riddle-messages-container">
                      {riddleMessages.length === 0 && (
                        <div className="message assistant riddle-intro">
                          <div className="message-content">
                            <span className="guardian-icon">🔮</span>
                            Welcome, seeker of ancient wisdom. I present to you this riddle:
                            <br /><br />
                            <em>{currentRiddle.riddle}</em>
                            <br /><br />
                            Speak your thoughts, and I shall guide your path to enlightenment.
                          </div>
                        </div>
                      )}
                      {riddleMessages.map((message, index) => (
                        <div key={index} className={`message ${message.role}`}>
                          <div className="message-content">
                            {message.role === 'assistant' && <span className="guardian-icon">🔮</span>}
                            {message.content}
                          </div>
                        </div>
                      ))}
                      {isRiddleChatLoading && (
                        <div className="message assistant">
                          <div className="message-content">
                            <span className="guardian-icon">🔮</span>
                            <span className="typing-indicator">The guardian contemplates...</span>
                          </div>
                        </div>
                      )}
                      <div ref={riddleMessagesEndRef} />
                    </div>
                    
                    <div className="riddle-input-container">
                      <textarea
                        value={riddleInputMessage}
                        onChange={(e) => setRiddleInputMessage(e.target.value)}
                        onKeyDown={handleRiddleChatKeyPress}
                        placeholder="Ask questions, make guesses, seek wisdom..."
                        disabled={isRiddleChatLoading}
                        rows={2}
                        className="riddle-chat-input"
                      />
                      <button 
                        onClick={sendRiddleMessage}
                        disabled={!riddleInputMessage.trim() || isRiddleChatLoading}
                        className="riddle-send-button"
                      >
                        🚀 Send
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {!riddleLoading && !riddleError && currentRiddle && currentRiddle.completed && (
                <div className="completion-section">
                  <h3>🎉 Congratulations!</h3>
                  <p>You've solved all the riddles! Check your coordinates above and verify them to claim your prize!</p>
                </div>
              )}
            </div>
          </div>
        

        
        <footer className="treasure-footer">
          <div className="footer-content">
            <p>Built by <a href="https://stratis-ai.com" target="_blank" rel="noopener noreferrer">Stratis AI</a></p>
            <p>Contact: <a href="mailto:info@aitreasuremap.com">info@aitreasuremap.com</a></p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
